import React, { useState, useEffect, useRef } from "react";
import { apiFetch } from "../../api/apiClient";

import {
  Box,
  Typography,
  useTheme,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import TableViewIcon from "@mui/icons-material/TableChart";
import CardViewIcon from "@mui/icons-material/ViewModule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Editor from "@monaco-editor/react";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../shared/sections/TitleSection";

export default function ApiRunLogsPage() {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
    border: "#2D2F31",
  };

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const resizingColumn = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const [viewMode, setViewMode] = useState("table"); // "table" or "card"
  const [jsonViewerOpen, setJsonViewerOpen] = useState(false);
  const [selectedJson, setSelectedJson] = useState(null);
  const [jsonViewerTitle, setJsonViewerTitle] = useState("");
  const [selectedRunId, setSelectedRunId] = useState("all");
  const [allRunLogs, setAllRunLogs] = useState([]);
  const [allSchemaOutputs, setAllSchemaOutputs] = useState([]);
  const [allModifications, setAllModifications] = useState([]);

  // Tab configuration for the three logging tables
  const logTabs = [
    { id: "run_log", label: "Run Log", key: "runLog", table: "aipc_run_log" },
    {
      id: "schema_outputs",
      label: "Schema Outputs",
      key: "schemaOutputs",
      table: "aipc_schema_outputs",
    },
    {
      id: "modifications",
      label: "Modifications",
      key: "modifications",
      table: "aipc_run_modifications",
    },
  ];

  useEffect(() => {
    loadAllLogs();
  }, []);

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleMouseMove = (e) => {
    if (resizingColumn.current) {
      const diff = e.clientX - startX.current;
      const newWidth = Math.max(80, startWidth.current + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn.current]: newWidth,
      }));
    }
  };

  const handleMouseUp = () => {
    resizingColumn.current = null;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const loadAllLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all three tables
      const response = await apiFetch("/api/admin/api-run-logs?limit=500");

      if (!response.ok) {
        throw new Error("Failed to load API run logs");
      }

      const data = await response.json();
      const logs = (data.run_logs || []).sort(
        (a, b) => new Date(b.run_timestamp).getTime() - new Date(a.run_timestamp).getTime(),
      );
      const outputs = data.schema_outputs || [];
      const mods = data.modifications || [];

      // Store all data
      setAllRunLogs(logs);
      setAllSchemaOutputs(outputs);
      setAllModifications(mods);

      // Set default to most recent run (first in list)
      if (logs.length > 0) {
        setSelectedRunId(logs[0].run_id);
      } else {
        setSelectedRunId("all");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error loading API run logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadAllLogs();
  };

  const handleDownloadCSV = (data, filename) => {
    if (!data || data.length === 0) return;

    // Convert to CSV
    const headers = Object.keys(data[0]).join(",");
    const rows = data.map((row) =>
      Object.values(row)
        .map((val) => {
          // Handle JSON objects/arrays
          if (typeof val === "object" && val !== null) {
            return `"${JSON.stringify(val).replace(/"/g, '""')}"`;
          }
          return `"${val}"`;
        })
        .join(","),
    );
    const csv = [headers, ...rows].join("\n");

    // Download
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getCurrentData = () => {
    // Filter data by selected run
    const filterByRun = (data) => {
      if (selectedRunId === "all") return data;
      return data.filter((item) => item.run_id === selectedRunId);
    };

    switch (activeTab) {
      case 0:
        // For run logs, show selected run only or all
        return selectedRunId === "all"
          ? allRunLogs
          : allRunLogs.filter((log) => log.run_id === selectedRunId);
      case 1:
        return filterByRun(allSchemaOutputs);
      case 2:
        return filterByRun(allModifications);
      default:
        return [];
    }
  };

  // Compute filtered data for display
  const runLogs = getCurrentData();
  const schemaOutputs = activeTab === 1 ? getCurrentData() : [];
  const modifications = activeTab === 2 ? getCurrentData() : [];

  const formatCellValue = (value, columnName) => {
    // Handle null/undefined
    if (value === null || value === undefined) return "-";

    // Handle JSON columns (display preview)
    const jsonColumns = [
      "request_payload",
      "user_questions",
      "existing_recipe",
      "existing_playbook",
      "db_output",
      "schema_output",
      "original_value",
      "new_value",
    ];

    if (jsonColumns.includes(columnName.toLowerCase())) {
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          const preview = JSON.stringify(parsed).substring(0, 100);
          return preview.length < JSON.stringify(parsed).length ? preview + "..." : preview;
        } catch {
          return value.substring(0, 100) + (value.length > 100 ? "..." : "");
        }
      }
      if (typeof value === "object") {
        const preview = JSON.stringify(value).substring(0, 100);
        return preview.length < JSON.stringify(value).length ? preview + "..." : preview;
      }
    }

    // Handle dates
    if (columnName.toLowerCase().includes("date") || columnName.toLowerCase().includes("time")) {
      if (typeof value === "string") {
        try {
          const date = new Date(value);
          return date.toLocaleString();
        } catch {
          return value;
        }
      }
    }

    // Handle numbers
    if (typeof value === "number") {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
    }

    return value.toString();
  };

  const handleViewJson = (jsonData, title) => {
    try {
      // Try to parse if it's a string
      const parsed = typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;
      setSelectedJson(parsed);
      setJsonViewerTitle(title);
      setJsonViewerOpen(true);
    } catch (error) {
      console.error("Failed to parse JSON:", error);
      setSelectedJson(jsonData);
      setJsonViewerTitle(title);
      setJsonViewerOpen(true);
    }
  };

  const renderStatusChip = (status) => {
    const statusColors = {
      success: { bg: "rgba(76, 175, 80, 0.2)", color: "#4caf50" },
      error: { bg: "rgba(244, 67, 54, 0.2)", color: "#f44336" },
      partial: { bg: "rgba(255, 152, 0, 0.2)", color: "#ff9800" },
    };

    const colors = statusColors[status?.toLowerCase()] || statusColors.partial;

    return (
      <Chip
        label={status}
        size="small"
        icon={status?.toLowerCase() === "success" ? <CheckCircleIcon /> : <ErrorIcon />}
        sx={{
          bgcolor: colors.bg,
          color: colors.color,
          fontWeight: 600,
          fontSize: "0.75rem",
          "& .MuiChip-icon": {
            color: colors.color,
          },
        }}
      />
    );
  };

  const renderCardView = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    const data = getCurrentData();

    if (!data || data.length === 0) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          No data available for {logTabs[activeTab].label}
        </Alert>
      );
    }

    const columns = Object.keys(data[0]);

    return (
      <Box
        sx={{
          p: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          width: "95vw",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 2,
            pt: 1,
            pb: 1,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: perlColors.textPrimary, fontWeight: 500 }}>
            {logTabs[activeTab].label} ({data.length} records)
          </Typography>
          <Tooltip title="Download as CSV">
            <IconButton
              onClick={() => handleDownloadCSV(data, logTabs[activeTab].key)}
              sx={{ color: perlColors.textPrimary }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            flex: 1,
            px: 2,
            pb: 2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            sx={{
              bgcolor: perlColors.background,
              border: `1px solid ${perlColors.border}`,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CardContent
              sx={{
                p: 2,
                "&:last-child": { pb: 2 },
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <TableContainer sx={{ flex: 1, overflowX: "auto", overflowY: "auto" }}>
                <Table
                  size="small"
                  sx={{ tableLayout: "fixed", width: "800px", minWidth: "800px" }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          color: perlColors.textPrimary,
                          fontWeight: 700,
                          borderBottom: `2px solid ${perlColors.border}`,
                          padding: "12px",
                          fontSize: "0.875rem",
                          whiteSpace: "nowrap",
                          bgcolor: "#2D2F31",
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          width: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        Field
                      </TableCell>
                      {data.map((_, idx) => (
                        <TableCell
                          key={idx}
                          sx={{
                            color: perlColors.textPrimary,
                            fontWeight: 700,
                            borderBottom: `2px solid ${perlColors.border}`,
                            padding: "12px",
                            fontSize: "0.875rem",
                            textAlign: "center",
                            bgcolor: "#2D2F31",
                            whiteSpace: "nowrap",
                            width: "300px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Record {idx + 1}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {columns.map((col) => (
                      <TableRow
                        key={col}
                        sx={{
                          "&:hover": { bgcolor: "rgba(255, 255, 255, 0.05)" },
                        }}
                      >
                        <TableCell
                          sx={{
                            color: perlColors.textPrimary,
                            fontWeight: 600,
                            borderBottom: `1px solid ${perlColors.border}`,
                            padding: "10px 12px",
                            fontSize: "0.875rem",
                            whiteSpace: "nowrap",
                            bgcolor: perlColors.background,
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            width: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={col}
                        >
                          {col}
                        </TableCell>
                        {data.map((row, idx) => {
                          const isJsonColumn = [
                            "request_payload",
                            "user_questions",
                            "schema_output",
                            "original_value",
                            "new_value",
                          ].includes(col.toLowerCase());

                          return (
                            <TableCell
                              key={idx}
                              sx={{
                                color: perlColors.textPrimary,
                                borderBottom: `1px solid ${perlColors.border}`,
                                padding: "10px 12px",
                                fontSize: "0.875rem",
                                textAlign: "center",
                                width: "300px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={formatCellValue(row[col], col)}
                            >
                              {col.toLowerCase() === "status" ? (
                                renderStatusChip(row[col])
                              ) : col.toLowerCase() === "response_status" ? (
                                renderStatusChip(row[col])
                              ) : isJsonColumn && row[col] ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.875rem",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      flex: 1,
                                    }}
                                  >
                                    {formatCellValue(row[col], col)}
                                  </Typography>
                                  <Tooltip title="View JSON">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleViewJson(row[col], `${col} - Record ${idx + 1}`)
                                      }
                                      sx={{
                                        color: "#ff6034",
                                        p: 0.5,
                                        "&:hover": {
                                          bgcolor: "rgba(255, 96, 52, 0.1)",
                                        },
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                formatCellValue(row[col], col)
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  };

  const renderTable = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    const data = getCurrentData();

    if (!data || data.length === 0) {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          No data available for {logTabs[activeTab].label}
        </Alert>
      );
    }

    const columns = Object.keys(data[0]);

    // Standard column widths based on data type and common patterns
    const getColumnWidth = (col) => {
      // Use stored width if available (after user resize)
      if (columnWidths[`${logTabs[activeTab].key}_${col}`]) {
        return columnWidths[`${logTabs[activeTab].key}_${col}`];
      }

      const colLower = col.toLowerCase();

      // UUID columns - medium
      if (colLower.includes("id") && colLower.includes("run")) {
        return 280;
      }

      // ID columns - narrow
      if (colLower.includes("id") || colLower === "output_id" || colLower === "modification_id") {
        return 100;
      }

      // Timestamps - medium
      if (colLower.includes("created") || colLower.includes("updated")) {
        return 180;
      }

      // Status - narrow
      if (colLower === "status") {
        return 100;
      }

      // Tower/Commodity/Schema Type - narrow
      if (
        colLower === "tower" ||
        colLower === "commodity" ||
        colLower === "schema_type" ||
        colLower === "execution_group"
      ) {
        return 120;
      }

      // Project/Part Key - medium
      if (colLower === "project_id" || colLower === "part_key" || colLower === "db_project_id") {
        return 150;
      }

      // Boolean flags - narrow
      if (colLower === "is_rerun") {
        return 90;
      }

      // Time/Duration columns - narrow
      if (
        colLower.includes("time") ||
        colLower.includes("duration") ||
        colLower.includes("seconds")
      ) {
        return 120;
      }

      // Cost columns - medium
      if (colLower.includes("cost")) {
        return 130;
      }

      // Error message - wide
      if (colLower.includes("error")) {
        return 250;
      }

      // JSON columns - very wide
      if (
        colLower.includes("payload") ||
        colLower.includes("output") ||
        colLower.includes("recipe") ||
        colLower.includes("playbook") ||
        colLower.includes("questions") ||
        colLower.includes("value")
      ) {
        return 300;
      }

      // Field name columns - medium
      if (colLower === "field_modified") {
        return 180;
      }

      // Default width
      return 160;
    };

    // Mouse down handler for column resize
    const handleMouseDown = (e, col) => {
      e.preventDefault();
      resizingColumn.current = `${logTabs[activeTab].key}_${col}`;
      startX.current = e.clientX;
      startWidth.current = getColumnWidth(col);
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    return (
      <Box sx={{ p: 0, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 2,
            pt: 1,
            pb: 1,
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ color: perlColors.textPrimary, fontWeight: 500 }}>
            {logTabs[activeTab].label} ({data.length} rows)
          </Typography>
          <Tooltip title="Download as CSV">
            <IconButton
              onClick={() => handleDownloadCSV(data, logTabs[activeTab].key)}
              sx={{ color: perlColors.textPrimary }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            bgcolor: perlColors.background,
            border: `1px solid ${perlColors.border}`,
            flex: 1,
            overflow: "auto",
            width: "1400px",
            minHeight: 0,
            maxHeight: "80%",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              width: "100%",
              tableLayout: "auto",
            }}
          >
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      bgcolor: "#2D2F31",
                      color: perlColors.textPrimary,
                      fontWeight: 600,
                      borderBottom: `2px solid ${perlColors.border}`,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: getColumnWidth(col),
                      minWidth: getColumnWidth(col),
                      maxWidth: getColumnWidth(col),
                      position: "relative",
                      padding: "6px 16px",
                    }}
                  >
                    {col}
                    {/* Resize handle */}
                    <Box
                      onMouseDown={(e) => handleMouseDown(e, col)}
                      sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: "5px",
                        cursor: "col-resize",
                        userSelect: "none",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.2)",
                        },
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    height: "36px",
                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.05)" },
                  }}
                >
                  {columns.map((col) => {
                    const isJsonColumn = [
                      "request_payload",
                      "user_questions",
                      "schema_output",
                      "original_value",
                      "new_value",
                    ].includes(col.toLowerCase());

                    return (
                      <TableCell
                        key={col}
                        sx={{
                          color: perlColors.textPrimary,
                          borderBottom: `1px solid ${perlColors.border}`,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: getColumnWidth(col),
                          minWidth: getColumnWidth(col),
                          maxWidth: getColumnWidth(col),
                          height: "36px",
                          padding: "6px 16px",
                        }}
                        title={formatCellValue(row[col], col)}
                      >
                        {col.toLowerCase() === "status" ? (
                          renderStatusChip(row[col])
                        ) : col.toLowerCase() === "response_status" ? (
                          renderStatusChip(row[col])
                        ) : isJsonColumn && row[col] ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                              }}
                            >
                              {formatCellValue(row[col], col)}
                            </Typography>
                            <Tooltip title="View JSON">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleViewJson(
                                    row[col],
                                    `${col} - ${row.schema_type || row.run_id || `Row ${idx + 1}`}`,
                                  )
                                }
                                sx={{
                                  color: "#ff6034",
                                  p: 0.5,
                                  "&:hover": {
                                    bgcolor: "rgba(255, 96, 52, 0.1)",
                                  },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          formatCellValue(row[col], col)
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Admin"
          industry="API Run Logs"
          commodity="Execution Audit Trail"
          tierImage="/images/Silver.jpg"
          tierLabel="Logs Viewer"
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${perlColors.border}`,
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ color: perlColors.textPrimary, fontWeight: 600, fontSize: "1.1rem" }}
          >
            API Run Logs Dashboard
          </Typography>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Run Filter Dropdown */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel
                sx={{
                  color: perlColors.textPrimary,
                  "&.Mui-focused": { color: "#ff6034" },
                }}
              >
                Filter by Run
              </InputLabel>
              <Select
                value={selectedRunId}
                onChange={(e) => setSelectedRunId(e.target.value)}
                label="Filter by Run"
                sx={{
                  color: perlColors.textPrimary,
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: perlColors.border,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ff6034",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ff6034",
                  },
                  ".MuiSvgIcon-root": {
                    color: perlColors.textPrimary,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "#2D2F31",
                      color: perlColors.textPrimary,
                      "& .MuiMenuItem-root": {
                        "&:hover": {
                          bgcolor: "rgba(255, 96, 52, 0.1)",
                        },
                        "&.Mui-selected": {
                          bgcolor: "rgba(255, 96, 52, 0.2)",
                          "&:hover": {
                            bgcolor: "rgba(255, 96, 52, 0.3)",
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="all">All Runs</MenuItem>
                {allRunLogs.map((log) => (
                  <MenuItem key={log.run_id} value={log.run_id}>
                    Run {log.run_id} - {new Date(log.run_timestamp).toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                }
              }}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  color: perlColors.textPrimary,
                  borderColor: perlColors.border,
                  "&.Mui-selected": {
                    bgcolor: "#ff6034",
                    color: "#fff",
                    "&:hover": {
                      bgcolor: "#e55530",
                    },
                  },
                  "&:hover": {
                    bgcolor: "rgba(255, 96, 52, 0.1)",
                  },
                },
              }}
            >
              <ToggleButton value="table" aria-label="table view">
                <Tooltip title="Table View">
                  <TableViewIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="card" aria-label="card view">
                <Tooltip title="Card View">
                  <CardViewIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            <Tooltip title="Refresh Logs">
              <IconButton
                onClick={handleRefresh}
                disabled={loading}
                sx={{ color: perlColors.textPrimary }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ borderBottom: `1px solid ${perlColors.border}`, px: 3, pt: 0, pb: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 42,
              "& .MuiTab-root": {
                color: perlColors.textPrimary,
                opacity: 0.7,
                "&.Mui-selected": {
                  color: "#ff6034",
                  opacity: 1,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#ff6034",
              },
            }}
          >
            {logTabs.map((tab) => (
              <Tab key={tab.id} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            p: 0,
          }}
        >
          {viewMode === "table" ? renderTable() : renderCardView()}
        </Box>

        {/* JSON Viewer Dialog */}
        <Dialog
          open={jsonViewerOpen}
          onClose={() => setJsonViewerOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: perlColors.background,
              border: `1px solid ${perlColors.border}`,
              minHeight: "70vh",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#2D2F31",
              color: perlColors.textPrimary,
              borderBottom: `1px solid ${perlColors.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {jsonViewerTitle}
            </Typography>
            <Tooltip title="Copy JSON">
              <IconButton
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(selectedJson, null, 2));
                }}
                sx={{ color: perlColors.textPrimary }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent
            sx={{
              bgcolor: perlColors.background,
              color: perlColors.textPrimary,
              p: 0,
              overflow: "hidden",
            }}
          >
            {selectedJson && (
              <Box sx={{ height: "70vh", width: "100%" }}>
                <Editor
                  height="100%"
                  language="json"
                  theme="vs-dark"
                  value={JSON.stringify(selectedJson, null, 2)}
                  options={{
                    readOnly: true,
                    minimap: { enabled: true },
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                    wordWrap: "on",
                    automaticLayout: true,
                    folding: true,
                    lineNumbers: "on",
                    renderWhitespace: "selection",
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              bgcolor: "#2D2F31",
              borderTop: `1px solid ${perlColors.border}`,
              p: 2,
            }}
          >
            <Button
              onClick={() => setJsonViewerOpen(false)}
              sx={{
                color: perlColors.textPrimary,
                "&:hover": {
                  bgcolor: "rgba(255, 96, 52, 0.1)",
                },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarLayout>
  );
}
